import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Receipt, Trash2, Calculator } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';

const EXPENSE_TYPES = [
  { value: 'ADM', label: 'Biaya ADM' },
  { value: 'GAJI', label: 'Biaya Gaji' },
  { value: 'KANTOR', label: 'Biaya Kantor' },
  { value: 'SEWA', label: 'Biaya Sewa' },
  { value: 'LAIN_LAIN', label: 'Biaya Lain-Lain' },
  { value: 'GEDUNG', label: 'Biaya Gedung' },
  { value: 'INVESTASI_MITRA', label: 'Biaya Investasi Mitra' }
];

const ACCOUNT_TYPES = [
  { value: 'CENTRAL', label: 'Rekening Pusat' },
  { value: 'CUSTOMER', label: 'Piutang' },
  { value: 'HUTANG_USAHA', label: 'Hutang Usaha' },
  { value: 'MANAGER', label: 'Laba Rugi Mitra' },
  { value: 'BRANCH', label: 'Rekening Cabang' }
];

const CentralExpenses = ({ user, onDataChange }) => {
  const [expenses, setExpenses] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [incomeBreakdown, setIncomeBreakdown] = useState([]);
  const [roundingDialogOpen, setRoundingDialogOpen] = useState(false);
  const [roundingLoading, setRoundingLoading] = useState(false);
  
  // Accounts for dropdown
  const [centralAccounts, setCentralAccounts] = useState([]);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [hutangAccounts, setHutangAccounts] = useState([]);
  const [managerAccounts, setManagerAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchAccounts, setBranchAccounts] = useState([]);
  const [bulkSelectedBranch, setBulkSelectedBranch] = useState('');
  const [bulkBranchAccounts, setBulkBranchAccounts] = useState([]);
  
  const [formData, setFormData] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    expense_type: '',
    account_type: '',
    account_id: '',
    amount: '',
    description: '',
    transaction_description: ''
  });
  
  // Bulk input state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    expense_type: '',
    account_type: '',
    account_id: '',
    transaction_description: '',
    items: [
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''}
    ]
  });

  useEffect(() => {
    fetchData();
    fetchAccounts();
  }, []);

  usePeriodChange(() => fetchData(), { includeSettings: true });

  useEffect(() => {
    // Load accounts based on selected account_type
    if (formData.account_type === 'BRANCH' && formData.account_id) {
      loadBranchAccounts(formData.account_id);
    }
  }, [formData.account_type, formData.account_id]);

  const fetchAccounts = async () => {
    try {
      const [centralRes, customerRes, hutangRes, managerRes, branchesRes] = await Promise.all([
        api.get('/central-accounts'),
        api.get('/customer-accounts'),
        api.get('/other-accounts'),
        api.get('/manager-accounts'),
        api.get('/branches')
      ]);
      
      setCentralAccounts(centralRes.data);
      setCustomerAccounts(customerRes.data);
      setHutangAccounts(hutangRes.data);
      setManagerAccounts(managerRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const loadBranchAccounts = async (branchId) => {
    try {
      const branchRes = await api.get(`/branches/${branchId}`);
      setBranchAccounts(branchRes.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch branch accounts:', error);
    }
  };

  const fetchData = async () => {
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      
      // Build query params
      const params = periodId ? { period_id: periodId } : {};
      
      const expensesRes = await api.get('/central-expenses', { params });
      setExpenses(expensesRes.data);
      
      try {
        const periodRes = await api.get('/periods/active');
        setActivePeriod(periodRes.data);
      } catch (error) {
        // No active period
      }
      
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        expense_date: new Date(formData.expense_date).toISOString(),
        period_id: localStorage.getItem('selectedPeriodId') || null
      };

      if (editingId) {
        await api.put(`/central-expenses/${editingId}`, submitData);
        toast.success('Biaya berhasil diperbarui');
      } else {
        await api.post('/central-expenses', submitData);
        toast.success('Biaya berhasil ditambahkan');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operasi gagal');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingId(expense.id);
    setFormData({
      expense_date: expense.expense_date?.split('T')[0] || '',
      expense_type: expense.expense_type,
      account_type: expense.account_type,
      account_id: expense.account_id,
      amount: expense.amount.toString(),
      description: expense.description
    });
    
    // Load branch accounts if account_type is BRANCH
    if (expense.account_type === 'BRANCH') {
      const branchId = expense.account_id.split('_')[0]; // Extract branch ID if stored
      // Or find branch that contains this account
      branches.forEach(branch => {
        if (branch.accounts && branch.accounts.find(acc => acc.id === expense.account_id)) {
          loadBranchAccounts(branch.id);
        }
      });
    }
    
    setDialogOpen(true);
  };

  const handleDeleteClick = (expenseId) => {
    setDeletingId(expenseId);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    setLoading(true);
    try {
      await api.delete(`/central-expenses/${deletingId}`);
      toast.success('Biaya berhasil dihapus');
      setDeleteDialogOpen(false);
      setDeletingId(null);
      fetchData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operasi gagal');
    } finally {
      setLoading(false);
    }
  };

  const handleRoundDecimals = async () => {
    setRoundingLoading(true);
    try {
      const res = await api.post('/central-expenses/round-decimals');
      const {
        investor_changes = 0, mitra_changes = 0,
        initial_changes = 0, inv_tx_changes = 0,
        mgr_tx_changes = 0, snapshot_changes = 0,
        rounding_diff = 0, message
      } = res.data || {};
      const total = investor_changes + mitra_changes + initial_changes
                  + inv_tx_changes + mgr_tx_changes + snapshot_changes;
      if (total === 0) {
        toast.info(message || 'Tidak ada nilai desimal yang perlu dibulatkan');
      } else {
        const parts = [];
        if (investor_changes) parts.push(`${investor_changes} saldo investor`);
        if (mitra_changes) parts.push(`${mitra_changes} saldo mitra`);
        if (initial_changes) parts.push(`${initial_changes} saldo awal`);
        if (inv_tx_changes) parts.push(`${inv_tx_changes} riwayat investor`);
        if (mgr_tx_changes) parts.push(`${mgr_tx_changes} riwayat mitra`);
        if (snapshot_changes) parts.push(`${snapshot_changes} snapshot periode`);
        toast.success(
          `${message}: ${parts.join(', ')}` +
          ` — Selisih Rp.\u00a0${rounding_diff.toLocaleString('id-ID')} ke Biaya Pusat`
        );
      }
      setRoundingDialogOpen(false);
      fetchData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal melakukan pembulatan desimal');
    } finally {
      setRoundingLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate
      if (!bulkFormData.expense_type || !bulkFormData.account_type || !bulkFormData.account_id || !bulkFormData.transaction_description) {
        toast.error('Mohon lengkapi semua field wajib');
        return;
      }
      
      if (bulkFormData.items.length === 0) {
        toast.error('Mohon tambahkan minimal 1 item biaya');
        return;
      }
      
      // Validate items
      for (let item of bulkFormData.items) {
        if (!item.expense_date || !item.amount || !item.description) {
          toast.error('Semua item harus diisi lengkap');
          return;
        }
      }

      const submitData = {
        ...bulkFormData,
        period_id: localStorage.getItem('selectedPeriodId') || null,
        items: bulkFormData.items.map(item => ({
          ...item,
          amount: parseFloat(item.amount),
          expense_date: new Date(item.expense_date).toISOString()
        }))
      };

      await api.post('/central-expenses/bulk', submitData);
      toast.success(`${bulkFormData.items.length} biaya berhasil ditambahkan`);
      
      setBulkDialogOpen(false);
      resetBulkForm();
      fetchData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operasi gagal');
    } finally {
      setLoading(false);
    }
  };
  
  const addBulkItem = () => {
    setBulkFormData({
      ...bulkFormData,
      items: [...bulkFormData.items, {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''}]
    });
  };
  
  const removeBulkItem = (index) => {
    setBulkFormData({
      ...bulkFormData,
      items: bulkFormData.items.filter((_, i) => i !== index)
    });
  };
  
  const updateBulkItem = (index, field, value) => {
    const newItems = [...bulkFormData.items];
    newItems[index][field] = value;
    setBulkFormData({
      ...bulkFormData,
      items: newItems
    });
  };


  const resetForm = () => {
    setFormData({
      expense_date: new Date().toISOString().split('T')[0],
      expense_type: '',
      account_type: '',
      account_id: '',
      amount: '',
      description: '',
      transaction_description: ''
    });
    setEditingId(null);
    setBranchAccounts([]);
  };
  
  const resetBulkForm = () => {
    setBulkFormData({
      expense_type: '',
      account_type: '',
      account_id: '',
      transaction_description: '',
      items: [
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''}
      ]
    });
    setBulkSelectedBranch('');
    setBulkBranchAccounts([]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('Rp', 'Rp.');
  };

  // Format number with thousand separator for input display
  const formatInputNumber = (value) => {
    if (!value && value !== 0) return '';
    // Remove non-numeric characters except minus sign
    const cleanValue = value.toString().replace(/[^\d-]/g, '');
    if (!cleanValue || cleanValue === '-') return cleanValue;
    
    // Handle negative numbers
    const isNegative = cleanValue.startsWith('-');
    const absValue = cleanValue.replace('-', '');
    
    // Format with thousand separator
    const formatted = absValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return isNegative ? `-${formatted}` : formatted;
  };

  // Parse formatted number back to numeric value
  const parseInputNumber = (value) => {
    if (!value) return '';
    // Remove thousand separator (.) but keep minus sign
    return value.toString().replace(/\./g, '');
  };
  
  // Calculate totals
  const calculateTotals = () => {
    // Pendapatan Biaya Operasional hanya dari Penjualan Cabang (bukan biaya minus)
    const operationalIncome = expenses.filter(e => e.amount < 0 && e.account_name === 'Pendapatan Biaya Operasional');
    
    // Semua biaya (termasuk yang minus) untuk perhitungan total biaya
    // Biaya minus yang bukan "Pendapatan Biaya Operasional" = biaya yang mengurangi total
    const allExpenses = expenses.filter(e => e.account_name !== 'Pendapatan Biaya Operasional');
    const totalCost = allExpenses.reduce((sum, e) => sum + e.amount, 0);
    
    const totalIncome = Math.abs(operationalIncome.reduce((sum, e) => sum + e.amount, 0));
    const netCost = totalCost - totalIncome;
    
    return { totalCost, totalIncome, netCost, operationalIncome };
  };
  
  const handleIncomeClick = () => {
    const { operationalIncome } = calculateTotals();
    
    // Group by cabang first, then by partai
    const breakdown = {};
    operationalIncome.forEach(exp => {
      // Extract cabang and partai from description like "Pendapatan dari Cabang - Partai X"
      const branchMatch = exp.description.match(/dari\s+([^-]+)\s+-/);
      const partaiMatch = exp.description.match(/Partai\s+(\w+)/);
      
      const branchName = branchMatch ? branchMatch[1].trim() : 'Unknown';
      const partai = partaiMatch ? partaiMatch[1] : 'Lainnya';
      
      if (!breakdown[branchName]) {
        breakdown[branchName] = {
          branchName,
          partais: {},
          totalAmount: 0
        };
      }
      
      if (!breakdown[branchName].partais[partai]) {
        breakdown[branchName].partais[partai] = {
          partai,
          amount: 0,
          items: []
        };
      }
      
      breakdown[branchName].partais[partai].amount += Math.abs(exp.amount);
      breakdown[branchName].partais[partai].items.push(exp);
      breakdown[branchName].totalAmount += Math.abs(exp.amount);
    });
    
    setIncomeBreakdown(Object.values(breakdown));
    setIncomeDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID');
  };

  // Get account options based on selected account_type (label: nama rekening + saldo)
  const getAccountOptions = () => {
    if (!formData.account_type) return [];

    const withBalance = (acc) => ({
      value: acc.id,
      label: `${acc.account_name} - ${formatCurrency(acc.balance || 0)}`
    });

    switch (formData.account_type) {
      case 'CENTRAL':
        return centralAccounts.map(withBalance);
      case 'CUSTOMER':
        return customerAccounts.map(withBalance);
      case 'HUTANG_USAHA':
        return hutangAccounts.map(withBalance);
      case 'MANAGER':
        return managerAccounts.map(withBalance);
      case 'BRANCH':
        return branches.map(branch => ({
          value: branch.id,
          label: branch.name
        }));
      default:
        return [];
    }
  };

  // Get branch account options (when account_type is BRANCH and branch is selected)
  const getBranchAccountOptions = () => {
    if (formData.account_type !== 'BRANCH') return [];
    return branchAccounts.map(acc => ({
      value: acc.id,
      label: `${acc.account_name || acc.name} - ${formatCurrency(acc.balance || 0)}`
    }));
  };

  // Handle account_type change
  const handleAccountTypeChange = (value) => {
    setFormData({
      ...formData,
      account_type: value,
      account_id: '' // Reset account_id when type changes
    });
    setBranchAccounts([]); // Clear branch accounts
  };

  // Handle account selection
  const handleAccountChange = (value) => {
    if (formData.account_type === 'BRANCH') {
      // If selecting branch, load its accounts
      loadBranchAccounts(value);
      setFormData({ ...formData, account_id: '' }); // Reset to select branch account next
    } else {
      setFormData({ ...formData, account_id: value });
    }
  };

  // Handle branch account selection (final selection for BRANCH type)
  const handleBranchAccountChange = (value) => {
    setFormData({ ...formData, account_id: value });
  };

  if (loading && expenses.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Receipt size={28} className="text-green-600" />
            Biaya Operasional Pusat
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Kelola biaya operasional kantor pusat
          </p>
        </div>
        {activePeriod && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="border-amber-600 text-amber-700 hover:bg-amber-50"
              onClick={() => setRoundingDialogOpen(true)}
              data-testid="round-decimals-btn"
            >
              <Calculator size={20} className="mr-2" />
              Pembulatan Desimal
            </Button>
            <Dialog open={bulkDialogOpen} onOpenChange={(open) => { setBulkDialogOpen(open); if (!open) resetBulkForm(); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                  <Receipt size={20} className="mr-2" />
                  Input Bulk
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle>Input Biaya Bulk</DialogTitle>
                    <div className="text-right">
                      <p className="text-sm text-slate-500 mb-1">Total Biaya</p>
                      {(() => {
                        const total = bulkFormData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                        return (
                          <p className={`text-2xl font-bold ${total < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(total)}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </DialogHeader>
                <form onSubmit={handleBulkSubmit} className="space-y-4">
                  {/* Form Header */}
                  <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                    <div>
                      <Label htmlFor="bulk_expense_type">Tipe Biaya *</Label>
                      <SearchableSelect
                        options={EXPENSE_TYPES}
                        value={bulkFormData.expense_type}
                        onValueChange={(value) => setBulkFormData({ ...bulkFormData, expense_type: value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="bulk_account_type">Tipe Rekening *</Label>
                      <SearchableSelect
                        options={ACCOUNT_TYPES}
                        value={bulkFormData.account_type}
                        onValueChange={(value) => { setBulkFormData({ ...bulkFormData, account_type: value, account_id: '' }); setBulkSelectedBranch(''); setBulkBranchAccounts([]); }}
                      />
                    </div>
                    
                    {bulkFormData.account_type && bulkFormData.account_type !== 'BRANCH' && (
                      <div className="col-span-2">
                        <Label htmlFor="bulk_account">Rekening *</Label>
                        <SearchableSelect
                          options={
                            bulkFormData.account_type === 'CENTRAL' ? centralAccounts.map(acc => ({ value: acc.id, label: `${acc.account_name} - ${formatCurrency(acc.balance || 0)}` })) :
                            bulkFormData.account_type === 'CUSTOMER' ? customerAccounts.map(acc => ({ value: acc.id, label: `${acc.account_name} - ${formatCurrency(acc.balance || 0)}` })) :
                            bulkFormData.account_type === 'HUTANG_USAHA' ? hutangAccounts.map(acc => ({ value: acc.id, label: `${acc.account_name} - ${formatCurrency(acc.balance || 0)}` })) :
                            bulkFormData.account_type === 'MANAGER' ? managerAccounts.map(acc => ({ value: acc.id, label: `${acc.account_name} - ${formatCurrency(acc.balance || 0)}` })) : []
                          }
                          value={bulkFormData.account_id}
                          onValueChange={(value) => setBulkFormData({ ...bulkFormData, account_id: value })}
                          data-testid="bulk-account-select"
                        />
                      </div>
                    )}

                    {bulkFormData.account_type === 'BRANCH' && (
                      <>
                        <div>
                          <Label htmlFor="bulk_branch">Cabang *</Label>
                          <SearchableSelect
                            options={branches.map(branch => ({ value: branch.id, label: branch.name }))}
                            value={bulkSelectedBranch}
                            onValueChange={async (value) => {
                              setBulkSelectedBranch(value);
                              setBulkFormData({ ...bulkFormData, account_id: '' });
                              try {
                                const res = await api.get(`/branches/${value}`);
                                setBulkBranchAccounts(res.data.accounts || []);
                              } catch (error) {
                                setBulkBranchAccounts([]);
                              }
                            }}
                            data-testid="bulk-branch-select"
                          />
                        </div>
                        <div>
                          <Label htmlFor="bulk_branch_account">Rekening Cabang *</Label>
                          <SearchableSelect
                            options={bulkBranchAccounts.map(acc => ({
                              value: acc.id,
                              label: `${acc.account_name || acc.name} - ${formatCurrency(acc.balance || 0)}`
                            }))}
                            value={bulkFormData.account_id}
                            onValueChange={(value) => setBulkFormData({ ...bulkFormData, account_id: value })}
                            data-testid="bulk-branch-account-select"
                          />
                        </div>
                      </>
                    )}
                    
                    <div className="col-span-2">
                      <Label htmlFor="bulk_transaction_desc">Keterangan Transaksi *</Label>
                      <Input
                        id="bulk_transaction_desc"
                        value={bulkFormData.transaction_description}
                        onChange={(e) => setBulkFormData({ ...bulkFormData, transaction_description: e.target.value })}
                        required
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Ini adalah keterangan GLOBAL untuk semua biaya dalam bulk ini
                      </p>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Daftar Biaya</Label>
                      <Button type="button" size="sm" variant="outline" onClick={addBulkItem}>
                        <Plus size={16} className="mr-1" />
                        Tambah Baris
                      </Button>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[150px]">Tanggal</TableHead>
                            <TableHead className="w-[150px]">Jumlah</TableHead>
                            <TableHead>Keterangan</TableHead>
                            <TableHead className="w-[80px]">Aksi</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bulkFormData.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Input
                                  type="date"
                                  value={item.expense_date}
                                  onChange={(e) => updateBulkItem(index, 'expense_date', e.target.value)}
                                  required
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="text"
                                  inputMode="text"
                                  value={formatInputNumber(item.amount)}
                                  onChange={(e) => updateBulkItem(index, 'amount', parseInputNumber(e.target.value))}
                                  required
                                  className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.description}
                                  onChange={(e) => updateBulkItem(index, 'description', e.target.value)}
                                  required
                                />
                              </TableCell>
                              <TableCell>
                                {bulkFormData.items.length > 1 && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeBulkItem(index)}
                                  >
                                    <Trash2 size={16} className="text-red-600" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>
                      Batal
                    </Button>
                    <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={loading}>
                      {loading ? 'Menyimpan...' : `Simpan ${bulkFormData.items.length} Biaya`}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700">
                  <Plus size={20} className="mr-2" />
                  Tambah Biaya Pusat
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit Biaya Pusat' : 'Tambah Biaya Pusat'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="expense_date">Tanggal *</Label>
                    <Input
                      id="expense_date"
                      type="date"
                      value={formData.expense_date}
                      onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="expense_type">Tipe Biaya *</Label>
                    <SearchableSelect
                      value={formData.expense_type}
                      onValueChange={(value) => setFormData({ ...formData, expense_type: value })}
                      placeholder="Pilih Tipe Biaya"
                      emptyText="Tipe tidak ditemukan"
                      options={EXPENSE_TYPES}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="account_type">Tipe Rekening *</Label>
                    <SearchableSelect
                      value={formData.account_type}
                      onValueChange={handleAccountTypeChange}
                      placeholder="Pilih Tipe Rekening"
                      emptyText="Tipe tidak ditemukan"
                      options={ACCOUNT_TYPES}
                      required
                    />
                  </div>
                  
                  {formData.account_type && formData.account_type !== 'BRANCH' && (
                    <div>
                      <Label htmlFor="account_id">Rekening *</Label>
                      <SearchableSelect
                        value={formData.account_id}
                        onValueChange={(value) => setFormData({ ...formData, account_id: value })}
                        placeholder="Pilih Rekening"
                        emptyText="Rekening tidak ditemukan"
                        options={getAccountOptions()}
                        required
                      />
                    </div>
                  )}
                  
                  {formData.account_type === 'BRANCH' && (
                    <>
                      <div>
                        <Label htmlFor="branch_select">Cabang *</Label>
                        <SearchableSelect
                          value={formData.account_id && branchAccounts.length === 0 ? '' : branches.find(b => b.accounts && b.accounts.find(acc => acc.id === formData.account_id))?.id || ''}
                          onValueChange={handleAccountChange}
                          placeholder="Pilih Cabang"
                          emptyText="Cabang tidak ditemukan"
                          options={branches.map(b => ({ value: b.id, label: b.name }))}
                          required
                        />
                      </div>
                      {branchAccounts.length > 0 && (
                        <div>
                          <Label htmlFor="branch_account">Rekening Cabang *</Label>
                          <SearchableSelect
                            value={formData.account_id}
                            onValueChange={handleBranchAccountChange}
                            placeholder="Pilih Rekening"
                            emptyText="Rekening tidak ditemukan"
                            options={getBranchAccountOptions()}
                            required
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Jumlah *</Label>
                    <Input
                      id="amount"
                      type="text"
                      inputMode="numeric"
                      value={formatInputNumber(formData.amount)}
                      onChange={(e) => setFormData({ ...formData, amount: parseInputNumber(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Keterangan *</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="transaction_description">Keterangan Transaksi (Opsional)</Label>
                  <Input
                    id="transaction_description"
                    value={formData.transaction_description}
                    onChange={(e) => setFormData({ ...formData, transaction_description: e.target.value })}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Jika tidak diisi, akan menggunakan keterangan biaya sebagai keterangan transaksi
                  </p>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Batal
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={loading}>
                    {loading ? 'Menyimpan...' : editingId ? 'Update' : 'Simpan'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>
      
      {/* Income Breakdown Dialog */}
      <Dialog open={incomeDialogOpen} onOpenChange={setIncomeDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Pendapatan Biaya Operasional</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Pendapatan dari biaya operasional yang dibebankan ke setiap partai
            </p>
            
            {incomeBreakdown.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Tidak ada data pendapatan
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cabang / Partai</TableHead>
                      <TableHead className="text-right">Jumlah</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {incomeBreakdown.map((branch, branchIndex) => (
                      <React.Fragment key={branchIndex}>
                        {/* Cabang Header */}
                        <TableRow className="bg-green-100 border-t-2 border-green-500">
                          <TableCell className="font-bold text-green-900 text-lg">
                            {branch.branchName}
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-900 text-lg">
                            {formatCurrency(branch.totalAmount)}
                          </TableCell>
                        </TableRow>
                        
                        {/* Partai Rows (Summary Only) */}
                        {Object.values(branch.partais).map((partai, partaiIndex) => (
                          <TableRow key={partaiIndex} className="hover:bg-blue-50">
                            <TableCell className="pl-8 font-medium text-blue-800">
                              Partai {partai.partai}
                            </TableCell>
                            <TableCell className="text-right font-medium text-blue-800">
                              {formatCurrency(partai.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                    
                    {/* Grand Total Row */}
                    <TableRow className="bg-slate-100 border-t-2 border-slate-300">
                      <TableCell className="font-bold text-lg">Total Pendapatan</TableCell>
                      <TableCell className="text-right font-bold text-xl text-blue-700">
                        {formatCurrency(incomeBreakdown.reduce((sum, branch) => sum + branch.totalAmount, 0))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncomeDialogOpen(false)}>
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!activePeriod && (
        <Card className="border-l-4 border-l-yellow-500 bg-yellow-50">
          <CardContent className="pt-6">
            <p className="text-yellow-800">Tidak ada periode aktif. Buat periode baru untuk mulai mencatat biaya.</p>
          </CardContent>
        </Card>
      )}
      
      {/* Summary Cards */}
      {expenses.length > 0 && (() => {
        const { totalCost, totalIncome, netCost } = calculateTotals();
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-600 mb-1">Total Biaya Operasional Pusat</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(totalCost)}</p>
              </CardContent>
            </Card>
            
            <Card 
              className="border-l-4 border-l-blue-500 cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={handleIncomeClick}
            >
              <CardContent className="pt-6">
                <p className="text-sm text-slate-600 mb-1">Pendapatan Biaya Operasional (Klik untuk detail)</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalIncome)}</p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-600 mb-1">Total Biaya Pusat (Setelah Potongan)</p>
                <p className="text-2xl font-bold text-orange-700">{formatCurrency(netCost)}</p>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {expenses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">Belum ada biaya operasional pusat.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Daftar Biaya Operasional Pusat</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Tipe Biaya</TableHead>
                    <TableHead>Rekening</TableHead>
                    <TableHead className="text-right">Jumlah</TableHead>
                    <TableHead>Keterangan</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Filter out "Pendapatan Biaya Operasional" only
                    // Show all other expenses (including minus amounts)
                    const expensesOnly = expenses.filter(e => e.account_name !== 'Pendapatan Biaya Operasional');
                    
                    // Group by expense_type
                    const groupedByType = expensesOnly.reduce((acc, expense) => {
                      const type = expense.expense_type || 'LAIN_LAIN';
                      if (!acc[type]) acc[type] = [];
                      acc[type].push(expense);
                      return acc;
                    }, {});

                    return Object.entries(groupedByType).map(([type, typeExpenses]) => {
                      const typeTotal = typeExpenses.reduce((sum, exp) => sum + exp.amount, 0);
                      const typeLabel = EXPENSE_TYPES.find(t => t.value === type)?.label || type;
                      
                      return (
                        <React.Fragment key={type}>
                          {/* Type Header */}
                          <TableRow className="bg-green-50 border-t-2 border-green-300">
                            <TableCell colSpan={6} className="font-bold text-green-700">
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-green-800">{typeLabel}</span>
                                <span className="font-semibold text-green-600">{formatCurrency(typeTotal)}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expenses in this type */}
                          {typeExpenses.map((expense) => (
                            <TableRow 
                              key={expense.id}
                              className="cursor-pointer hover:bg-blue-50"
                              onClick={() => handleEdit(expense)}
                            >
                              <TableCell>{formatDate(expense.expense_date)}</TableCell>
                              <TableCell className="text-xs text-slate-600">{typeLabel}</TableCell>
                              <TableCell className="text-sm">{expense.account_name || '-'}</TableCell>
                              <TableCell className={`text-right font-bold ${expense.amount < 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(expense.amount)}</TableCell>
                              <TableCell className="text-slate-600 text-sm">{expense.description}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(expense.id);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    });
                  })()}
                  
                  {/* Summary Rows */}
                  {(() => {
                    const { totalCost, totalIncome, netCost } = calculateTotals();
                    return (
                      <>
                        {/* Total Biaya Operasional Pusat */}
                        <TableRow className="bg-green-50 border-t-2 border-green-400">
                          <TableCell colSpan={3} className="text-right font-bold text-green-800">
                            Total Biaya Operasional Pusat
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-700 text-lg">
                            {formatCurrency(totalCost)}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                        
                        {/* Total Pendapatan Biaya Operasional (Clickable) */}
                        <TableRow 
                          className="bg-blue-50 border-t border-blue-300 cursor-pointer hover:bg-blue-100 transition-colors"
                          onClick={handleIncomeClick}
                        >
                          <TableCell colSpan={3} className="text-right font-bold text-blue-800">
                            Total Pendapatan Biaya Operasional
                            <span className="text-xs ml-2 text-blue-600">(Klik untuk detail)</span>
                          </TableCell>
                          <TableCell className="text-right font-bold text-blue-700 text-lg">
                            {formatCurrency(totalIncome)}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                        
                        {/* Total Biaya Pusat (Setelah Potongan) */}
                        <TableRow className="bg-orange-50 border-t border-orange-300">
                          <TableCell colSpan={3} className="text-right font-bold text-orange-800">
                            Total Biaya Pusat (Setelah Potongan)
                          </TableCell>
                          <TableCell className="text-right font-bold text-orange-700 text-xl">
                            {formatCurrency(netCost)}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <p>Yakin ingin menghapus biaya ini? Saldo rekening akan dikembalikan.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Batal</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Menghapus...' : 'Hapus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Round Decimals Confirmation Dialog */}
      <AlertDialog open={roundingDialogOpen} onOpenChange={setRoundingDialogOpen}>
        <AlertDialogContent data-testid="round-decimals-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Pembulatan Desimal</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  Tindakan ini akan <strong>memotong angka desimal</strong> pada
                  seluruh saldo Investor dan Laba Rugi Mitra.
                </p>
                <p>
                  Selisih pembulatan akan otomatis dicatat sebagai
                  <strong> Biaya Pusat (Tipe: Lain-Lain)</strong> pada periode aktif.
                </p>
                <p className="text-amber-700 font-medium">
                  Tindakan ini tidak dapat dibatalkan. Lanjutkan?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="round-decimals-cancel-btn">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRoundDecimals}
              disabled={roundingLoading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="round-decimals-confirm-btn"
            >
              {roundingLoading ? 'Memproses...' : 'Ya, Bulatkan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CentralExpenses;
